"use client";

import { Component, type ReactNode } from "react";

import { clientLogger } from "@/lib/logging/client-logger";

type Props = { children: ReactNode };
type State = { hasError: boolean };

/**
 * Diagnostic error boundary that captures React's *component stack*
 * (errorInfo.componentStack) for render-phase faults — the one piece the
 * global-error boundary and window.onerror handler don't record.
 *
 * Render-phase errors like "Maximum update depth exceeded" (#185) and
 * "Rendered more hooks than the previous render" (#310) throw from inside
 * React/Radix internals, so the JS stack only ever names framework code. The
 * component stack is what points at the offending app component. With
 * productionBrowserSourceMaps enabled, React 19's component-stack frames carry
 * source positions that resolve to real file:line.
 *
 * This is a temporary diagnostic for the workspace-tab hooks crash; remove it
 * (and productionBrowserSourceMaps) once that bug is pinned. It logs and shows
 * a minimal reload fallback rather than swallowing silently.
 */
export class ComponentStackBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    // The server ingest drops any context whose JSON exceeds
    // MAX_CLIENT_LOG_CONTEXT_CHARS (4000). A full React componentStack runs
    // ~7KB, so trim to the innermost frames — those name the failing component
    // and its nearest ancestors — and omit the framework-only error.stack to
    // stay within budget.
    const componentStack = (info?.componentStack ?? "").slice(0, 3200);
    clientLogger.error("React render error (component-stack boundary)", {
      kind: "component-stack-boundary",
      name: error.name,
      message: error.message.slice(0, 300),
      componentStack,
    });
    clientLogger.flush();
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ fontFamily: "system-ui, sans-serif", padding: "2rem" }}>
          <h2>Something went wrong</h2>
          <p>The error has been reported. Reload to try again.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{ marginTop: "1rem", padding: "0.5rem 1rem" }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
